/**
 * Optional per-company columns in the documents table (คอลัมน์เสริมรายบริษัท)
 *
 * Some customers carry a business identifier in `@DocData` that only means
 * something to them — an insurer's policy number, for example. Showing it to
 * every company would add a permanently empty column to everyone else's table,
 * and hardcoding a customer's companyID in a .vue file means a deploy every
 * time another customer wants the same thing.
 *
 * So the column list is configuration, read from Settings/laoEtax alongside the
 * doc-type allow-list:
 *
 *   Settings/laoEtax.extraColumns["<companyID>"] = ["policyNo"]
 *   Settings/laoEtax.defaultExtraColumns         = []          // everyone else
 *
 * Only ids declared in LAO_EXTRA_COLUMNS below are honoured. An unknown id is
 * dropped rather than rendered: the key also feeds the Firestore range query
 * behind the search box, and a typo'd field path would fail the whole document
 * query, not just one column.
 *
 * These columns are searchable but NOT sortable. Firestore `orderBy` skips
 * documents that lack the field, and an optional column is by definition
 * missing from some documents — sorting by it would quietly drop those rows
 * from a table of tax documents. table.vue therefore renders these headers
 * without the sort affordance every other column has.
 *
 * Settings/laoEtax is world-readable (firestore.rules `match /Settings`), so
 * the client reads it directly — same as resolveAllowedLaoDocTypes.
 *
 * Adding a column here is NOT enough on its own: searching it needs an
 * ASCENDING composite index on `@DocType` + the field. Those are created BY
 * HAND in the Firebase console (Firestore -> Indexes), not deployed from
 * firebase/firestore.indexes.json — that file is a snapshot of the console and
 * several hundred indexes behind it, so an entry added there is documentation,
 * not a live index. A search made while the "กรอง" menu is narrowed also
 * touches `@DocData`.docStatus / `@DownloadedXml`; those combinations have no
 * index yet and Firestore answers with a one-click create link if anyone hits
 * one, which is how the rest of this table's indexes came to exist.
 */

/**
 * @typedef {Object} LaoExtraColumn
 * @property {string} id     key inside Settings/laoEtax.extraColumns
 * @property {string} key    Firestore field path, also the sort/search key
 * @property {string} label  Thai column header
 */

/** @type {LaoExtraColumn[]} */
export const LAO_EXTRA_COLUMNS = [
  {
    id: "policyNo",
    key: "@DocData.policyNo",
    label: "เลขที่สัญญา",
  },
];

// Every key this module may inject, so a component can strip its own previous
// injection when the company changes without having to remember what it added.
export const LAO_EXTRA_COLUMN_KEYS = LAO_EXTRA_COLUMNS.map((c) => c.key);

function columnsByIds(raw) {
  if (!Array.isArray(raw)) return null;
  const out = [];
  raw.forEach((v) => {
    const id = String(v || "").trim();
    const col = LAO_EXTRA_COLUMNS.find((c) => c.id === id);
    if (col && out.indexOf(col) === -1) out.push(col);
  });
  return out;
}

/**
 * Resolve the optional columns for one company.
 *
 * Returns [] — no extra columns — whenever the setting is absent or unreadable.
 * Unlike the doc-type allow-list this is cosmetic, so failing closed costs the
 * user a column, never a wrong filing.
 *
 * @param {firebase.firestore.Firestore} db
 * @param {string} companyID
 * @returns {Promise<LaoExtraColumn[]>}
 */
export async function resolveLaoExtraColumns(db, companyID) {
  try {
    const snap = await db.collection("Settings").doc("laoEtax").get();
    if (!snap.exists) return [];

    const data = snap.data() || {};
    const cid = (companyID || "").toString().trim();
    const map = data.extraColumns;
    if (
      cid &&
      map &&
      typeof map === "object" &&
      Object.prototype.hasOwnProperty.call(map, cid)
    ) {
      const forCompany = columnsByIds(map[cid]);
      if (forCompany) return forCompany;
    }

    const fallback = columnsByIds(data.defaultExtraColumns);
    if (fallback) return fallback;

    return [];
  } catch (e) {
    console.error("resolveLaoExtraColumns: Settings/laoEtax read failed", e);
    return [];
  }
}

export default {
  LAO_EXTRA_COLUMNS,
  LAO_EXTRA_COLUMN_KEYS,
  resolveLaoExtraColumns,
};
